import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import type { MenuDataItem } from '@ant-design/pro-components';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

import { ToastContainer } from 'react-toastify';
import { API } from './helpers/api';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

// 新增：获取 配置显示（自动带 token）
async function fetchVisibleConfig() {
  try {
    const res = await API.get('/api/user/config/visible');
    if (res.data?.data) return res.data.data;
    return res.data;
  } catch (e) {
    return undefined;
  }
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
  branding?: Record<string, any>;
  visibleConfig?: any;
}> {
  const fetchUserInfo = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        return undefined;
      }
      const parsedUser: any = JSON.parse(userStr);
      return {
        name: parsedUser.username,
        username: parsedUser.username, // 显式设置 username 字段
        nickname: parsedUser.nickname || parsedUser.username, // 优先 nickname，没有则用 username
        avatar:
          'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
        userid: '00000001',
        email: parsedUser.email,
        tags: [],
        role: parsedUser.role, // 新增 role 字段
        access: parsedUser.role, // access 字段同步为 role
        geographic: {},
        address: '',
        phone: '',
      };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // load runtime branding from public/branding.json (fallback to defaults in config/defaultSettings)
  const loadBranding = async () => {
    try {
      // detect locale from common app locations, then navigator, fallback to zh-CN
      const rawLocale =
        (window as any).__umi_locale ||
        localStorage.getItem('umi_locale') ||
        navigator.language ||
        'zh-CN';
      const locale = String(rawLocale || 'zh-CN').replace('_', '-');
      const parts = locale.split('-');
      const full = locale;
      const lang = parts[0];

      const candidates: string[] = [];
      candidates.push(`/branding.${full}.json`);
      if (lang && lang !== full) candidates.push(`/branding.${lang}.json`);
      candidates.push('/branding.json');

      for (const url of candidates) {
        try {
          const res = await fetch(url, { cache: 'no-cache' });
          if (!res.ok) continue;
          const json = await res.json();
          return json;
        } catch (e) {
          // ignore error, try next candidate
        }
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const [currentUser, branding, visibleConfig] = await Promise.all([
      fetchUserInfo(),
      loadBranding(),
      fetchVisibleConfig(),
    ]);
    return {
      fetchUserInfo,
      currentUser,
      branding,
      visibleConfig,
      settings: { ...(defaultSettings as Partial<LayoutSettings>) },
    };
  }

  const branding = await loadBranding();
  return {
    fetchUserInfo,
    branding,
    visibleConfig: undefined,
    settings: { ...(defaultSettings as Partial<LayoutSettings>) },
  };
}

// 递归处理菜单数据，动态设置 speakers 和 recorderManage 的 hideInMenu
function patchMenuData(items: MenuDataItem[], speakerDetect: boolean, recorderManagerEnable: boolean, voiceCloneEnable: boolean): MenuDataItem[] {
  return items.map(item => {
    let newItem = { ...item };
    if (item.path === '/console/speakers') {
      newItem.hideInMenu = !speakerDetect;
    }
    if (item.path === '/admin/recorder') {
      newItem.hideInMenu = !recorderManagerEnable;
    }
    if (item.path === '/console/voiceClone') {
      newItem.hideInMenu = !voiceCloneEnable;
    }
    if (item.routes) {
      (newItem as any).routes = patchMenuData(item.routes, speakerDetect, recorderManagerEnable, voiceCloneEnable);
    }
    if (item.children) {
      newItem.children = patchMenuData(item.children, speakerDetect, recorderManagerEnable, voiceCloneEnable);
    }
    return newItem;
  });
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 动态获取声纹识别开关和录音管理开关，兼容多种字段和类型
  const appConfig = initialState?.visibleConfig;
  const speakerDetect =
    appConfig?.SpeakerDetect === true ||
    appConfig?.SpeakerDetect === 'true';
  const recorderManagerEnable =
    appConfig?.RecorderManagerEnable === true ||
    appConfig?.RecorderManagerEnable === 'true';
  const voiceCloneEnable =
    appConfig?.VoiceCloneEnable === true ||
    appConfig?.VoiceCloneEnable === 'true';
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      // content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // bgLayoutImgList: [
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
    //     left: 85,
    //     bottom: 100,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
    //     bottom: -68,
    //     right: -45,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
    //     bottom: 0,
    //     left: 0,
    //     width: '331px',
    //   },
    // ],
    links: isDev ? [] : [],
  menuHeaderRender: undefined,
  menuDataRender: (menuData) => patchMenuData(menuData, speakerDetect, recorderManagerEnable, voiceCloneEnable),
    childrenRender: (children: React.ReactNode) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    // Prefer branding values from runtime branding (public/branding.json) when available.
    title: initialState?.branding?.title || initialState?.settings?.title,
    // @ts-ignore - logo may not exist on ProSettings type; provide at runtime
    logo: initialState?.branding?.logoUrl || (initialState?.settings as any)?.logo,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
